import { getClientInfoFromCookie } from '@commenda/auth';
import { FeatureLaunch, isFeatureLaunched } from '@commenda/feature-launches';
import { Dropdown, Icons, useToggle } from '@commenda/kakashi';
import { TComplianceItem } from '@commenda/types/src/compliance-calendar/compliances/types';
import { AppType, downloadFileFromFileId, useApp } from '@commenda/utils';
import { ComplianceManagedBy, ComplianceStatus } from '@prisma/client';
import { Country as CountryPkg } from 'country-state-city';
import { ManageAssigneesModal } from '../Assignees/ManageAssigneesModal';
import { CATEGORY_LABEL } from '../Calendar/calendar.constants';
import ArchiveFilingModal from './modals/ArchiveFilingModal';
import CompleteFilingModal from './modals/CompleteFilingModal';
import { PurchaseFilingModal } from './modals/PurchaseFilingModal';
import RequestExtensionModal from './modals/RequestExtensionModal';
import UnarchiveFilingModal from './modals/UnarchiveFilingModal';

const FilingDropdown = ({
  filing,
  refreshCompliances,
  loggedInUserEmail,
  onEditFiling,
  onExtendFiling,
}: {
  filing: TComplianceItem;
  refreshCompliances: () => void;
  loggedInUserEmail: string;
  onEditFiling: (filing: TComplianceItem) => void;
  onExtendFiling: (filing: TComplianceItem) => void;
}) => {
  const manageAssigneesModalVisibility = useToggle();
  const completeFilingModalVisibility = useToggle();
  const archiveFilingModalVisibility = useToggle();
  const unarchiveFilingModalVisibility = useToggle();
  const purchaseFilingModalVisibility = useToggle();
  const requestExtensionModalVisibility = useToggle();

  const isFilingCompletedOrFiled =
    filing.status === ComplianceStatus.COMPLETED ||
    filing.status === ComplianceStatus.FILED;

  const isFilingArchived = filing.status === ComplianceStatus.ARCHIVED;
  // ComplianceStatus.EXTENDED isn't in the generated @prisma/client enum yet
  // on this workstation; runtime value is the string 'EXTENDED'.
  const isFilingExtended = filing.status === ComplianceStatus.EXTENDED;
  const isCustomFiling = filing.isCustom === true;

  const { appType } = useApp();

  // Extension gating — strict matrix:
  //
  //   SR?    managedBy   agentFirm   Viewer           Action
  //   ----   ---------   ---------   --------------   -----------
  //   any    CLIENT      —           any              Extend
  //   no     COMMENDA    —           Commenda admin   Extend
  //   no     COMMENDA    —           client           Request
  //   yes    COMMENDA    yes         agent/admin app  Extend
  //   yes    COMMENDA    yes         client           Request
  //   yes    COMMENDA    no          any              none
  //
  // Client-managed extensions update the compliance item directly. A managed filing with a
  // request needs its assigned firm; without one, a Commenda admin extends it (from the client's
  // view of the filing, as no admin page shows filings), and a client's request goes to the
  // filing's own thread.
  //
  // "Actor app" = the viewer is the one acting on the filing, not requesting it.
  // Agents extend on behalf of their clients; admins extend on behalf of anyone.
  const isInExtendableState =
    !isFilingCompletedOrFiled && !isFilingArchived && !isFilingExtended;
  const hasServiceRequest = Boolean(filing.serviceRequestId);
  const isActorApp = appType === AppType.AGENT || appType === AppType.ADMIN;
  const isCommendaManaged = filing.managedBy === ComplianceManagedBy.COMMENDA;
  const isClientManaged = filing.managedBy === ComplianceManagedBy.CLIENT;
  const hasAssignedAgentFirm = Boolean(filing.assignedAgentFirmId);
  const isCommendaAdmin = Boolean(
    getClientInfoFromCookie()?.user?.isCommendaAdmin
  );

  const isExtensionLaunched = isFeatureLaunched(
    FeatureLaunch.COMPLIANCE_EXTENSION
  );
  const canExtendDirectly =
    isExtensionLaunched &&
    isInExtendableState &&
    (isClientManaged ||
      (isCommendaManaged &&
        (hasServiceRequest
          ? hasAssignedAgentFirm && isActorApp
          : isCommendaAdmin)));
  const canRequestExtension =
    isExtensionLaunched &&
    isInExtendableState &&
    isCommendaManaged &&
    (hasServiceRequest ? hasAssignedAgentFirm : !isCommendaAdmin) &&
    !isActorApp;

  const isDisabled =
    appType === AppType.ACCOUNTANT || appType === AppType.AGENT;

  return (
    <>
      <Dropdown
        menu={{
          groups: [
            {
              items: [
                isFilingCompletedOrFiled && {
                  label: 'Download receipt',
                  icon: Icons.Download01,
                  onClick: () =>
                    downloadFileFromFileId(
                      filing.acknowledgements[0].id,
                      filing.acknowledgements[0].fileName
                    ),
                },
                !isFilingCompletedOrFiled &&
                  !isFilingArchived && {
                    label: 'Upload receipt',
                    icon: Icons.Upload01,
                    onClick: completeFilingModalVisibility.show,
                  },
                // isPurchaseFilingSupported &&
                //   !isFilingCompletedOrFiled &&
                //   !hasPurchasedFiling && {
                //     label: 'Purchase filing',
                //     icon: Icons.ShoppingCart01,
                //     onClick: purchaseFilingModalVisibility.show,
                //   },
                !isDisabled && {
                  label:
                    isFilingCompletedOrFiled || isFilingArchived
                      ? 'View assignees'
                      : 'Manage assignees',
                  icon: Icons.Users01,
                  onClick: manageAssigneesModalVisibility.show,
                },
                isCustomFiling &&
                  !isFilingCompletedOrFiled &&
                  !isFilingArchived && {
                    label: 'Edit filing',
                    icon: Icons.Edit01,
                    onClick: () => onEditFiling(filing),
                  },
                canExtendDirectly && {
                  label: 'Extend filing',
                  icon: Icons.Calendar,
                  onClick: () => onExtendFiling(filing),
                },
                canRequestExtension && {
                  label: 'Request for extension',
                  icon: Icons.Calendar,
                  onClick: requestExtensionModalVisibility.show,
                },
                !isFilingCompletedOrFiled &&
                  !isFilingArchived && {
                    label: 'Archive filing',
                    icon: Icons.Archive,
                    onClick: archiveFilingModalVisibility.show,
                    labelClassName: 'text-utility-error-600',
                    iconClassName: 'text-utility-error-600',
                  },
                isFilingArchived && {
                  label: 'Unarchive',
                  icon: Icons.RefreshCw01,
                  onClick: unarchiveFilingModalVisibility.show,
                },
              ].filter(Boolean),
            },
          ],
        }}
        align="end"
      >
        <div className="size-8 flex justify-center items-center group">
          <Icons.DotsVertical className="size-4 text-fg-quaternary group-hover:text-fg-quaternary-hover" />
        </div>
      </Dropdown>
      <ManageAssigneesModal
        isOpen={manageAssigneesModalVisibility.visible}
        onClose={manageAssigneesModalVisibility.off}
        filing={filing}
        refreshCompliances={refreshCompliances}
        loggedInUserEmail={loggedInUserEmail}
      />
      <CompleteFilingModal
        isOpen={completeFilingModalVisibility.visible}
        onClose={completeFilingModalVisibility.hide}
        filingId={filing.id}
        companyId={filing.company.id}
        serviceRequestId={filing.serviceRequestId}
        onComplete={async () => {
          await refreshCompliances();
        }}
      />
      <ArchiveFilingModal
        isOpen={archiveFilingModalVisibility.visible}
        onClose={archiveFilingModalVisibility.toggle}
        filingId={filing.id}
        filingTitle={filing.title}
        entityName={filing.corporation?.companyDisplayName}
        isCustom={filing.isCustom}
        onSuccess={refreshCompliances}
      />
      <UnarchiveFilingModal
        isOpen={unarchiveFilingModalVisibility.visible}
        onClose={unarchiveFilingModalVisibility.toggle}
        filing={filing}
        onSuccess={refreshCompliances}
      />
      <PurchaseFilingModal
        isOpen={purchaseFilingModalVisibility.visible}
        onClose={purchaseFilingModalVisibility.toggle}
        filing={filing}
      />
      <RequestExtensionModal
        isOpen={requestExtensionModalVisibility.visible}
        onClose={requestExtensionModalVisibility.hide}
        filingId={filing.id}
        filingTitle={filing.title}
        entityName={filing.corporation?.companyDisplayName}
        jurisdiction={
          filing.corporation?.incorporationCountry
            ? (CountryPkg.getCountryByCode(
                filing.corporation.incorporationCountry
              )?.name ?? filing.corporation.incorporationCountry)
            : undefined
        }
        category={
          filing.category
            ? (CATEGORY_LABEL[filing.category] ?? filing.category)
            : undefined
        }
        syntheticStatus={filing.syntheticStatus}
        dueDate={filing.dueDate}
        onSuccess={refreshCompliances}
      />
    </>
  );
};

export { FilingDropdown };
